import { useEffect, useState } from 'react';
import { getVenuesAvailability } from '@/lib/api/public';
import { toVenueList, unwrap } from '@/lib/api/publicData';

/**
 * One truth about inventory for the marketing pages (owner upgrade brief 2026-09-27 §2.8): a
 * page never promises what the product then refuses. GET /api/venues/availability lists the
 * venues that sell places (slot_model_enabled); the free listing picks from the same list, so
 * "free listings are open" = at least one such venue.
 *
 * One request per page load (a module-level promise; a failure is retried on the next use).
 */
let pending = null;

/** The venues that sell places now (a promise of the parsed list). */
export function loadSellingVenues() {
  if (!pending) {
    pending = Promise.resolve()
      .then(() => getVenuesAvailability())
      .then((res) => toVenueList(unwrap(res)))
      .catch((err) => {
        pending = null;
        throw err;
      });
  }
  return pending;
}

/** Tests: forget the cached answer. */
export function resetInventory() {
  pending = null;
}

/**
 * 'open' (a venue takes free listings), 'closed' (none does yet) or 'unknown' (loading, or
 * the read failed: pages then keep their standing copy rather than guess).
 */
export function useFreeListingState() {
  const [state, setState] = useState('unknown');
  useEffect(() => {
    let alive = true;
    loadSellingVenues()
      .then((venues) => { if (alive) setState(venues.length > 0 ? 'open' : 'closed'); })
      .catch(() => { if (alive) setState('unknown'); });
    return () => { alive = false; };
  }, []);
  return state;
}

/** The free-listing note under a band, by state (the closed wording is the brief's). */
export const FREE_LISTING_CLOSED_NOTE = 'Free listings open when a venue near you joins. Reserve yours now: tell us your town and we’ll hold your place.';
